import {
  GoogleSyncError,
  refreshToken,
  refreshViaProxy,
  revokeToken,
  SyncError,
  type GoogleTokens,
} from "@app/storage";
import { create } from "zustand";
import { getServices } from "../platform.js";
import { activeGoogleClientId, googleConfigured, googleOAuthConfig } from "../lib/google-config.js";
import { DEFAULT_SYNC_URL } from "../lib/sync-server.js";
import { runOAuthFlow, GoogleOAuthError } from "../lib/google-oauth/index.js";
import { exchangeAuthCode, usesProxy } from "../lib/google-oauth/exchange.js";
import { toast } from "./toast.store.js";
import { tr } from "../lib/tr.js";

async function serverUrl(): Promise<string> {
  return (await getServices().settings.get("sync_url")) || DEFAULT_SYNC_URL;
}

const K_ACCESS = "google_access_token";
const K_REFRESH = "google_refresh_token";
const K_EXPIRY = "google_token_expiry";
const K_EMAIL = "google_account_email";

interface GoogleAuthState {
  connected: boolean;
  email: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  /** Epoch-ms access-token expiry (best-effort; the 401 path is authoritative). */
  expiry: number | null;
  busy: boolean;
  loaded: boolean;
  load: () => Promise<void>;
  connect: () => Promise<boolean>;
  disconnect: () => Promise<void>;
  /** Sync getter for the calendar client (returns the current token, maybe stale). */
  getAccessToken: () => string | null;
  /** Async refresh hook for the client's 401 retry: fresh access token or null. */
  refreshAccess: () => Promise<string | null>;
}

/**
 * Google account/connection state for Calendar sync. Tokens live in settings on
 * THIS device only and are NEVER added to `gatherPreferences()` (same rule as the
 * `auth_*` keys), so they never enter a backup or multi-device sync. PKCE public
 * client — no secret. Mirrors `auth.store.ts`.
 */
export const useGoogleAuthStore = create<GoogleAuthState>((set, get) => ({
  connected: false,
  email: null,
  accessToken: null,
  refreshToken: null,
  expiry: null,
  busy: false,
  loaded: false,

  async load() {
    const s = getServices().settings;
    const accessToken = (await s.get(K_ACCESS)) || null;
    const refreshTok = (await s.get(K_REFRESH)) || null;
    const email = (await s.get(K_EMAIL)) || null;
    const expiry = Number((await s.get(K_EXPIRY)) || 0) || null;
    set({ accessToken, refreshToken: refreshTok, email, expiry, connected: Boolean(refreshTok), loaded: true });
  },

  async connect() {
    if (!googleConfigured()) {
      toast.error(tr("toast.google.notConfigured"));
      return false;
    }
    set({ busy: true });
    try {
      const result = await runOAuthFlow(googleOAuthConfig());
      let url = "";
      if (usesProxy()) {
        url = await serverUrl();
        if (!url) {
          toast.error(tr("toast.google.setUrlFirst"));
          return false;
        }
      }
      const tokens: GoogleTokens = await exchangeAuthCode({
        serverUrl: url,
        clientId: activeGoogleClientId(),
        result,
      });
      const refreshTok = tokens.refresh_token || get().refreshToken; // keep existing if omitted
      if (!refreshTok) {
        toast.error(tr("toast.google.noRefreshToken"));
        return false;
      }
      const email = emailFromIdToken(tokens.id_token) ?? get().email;
      const expiry = Date.now() + (tokens.expires_in ?? 3600) * 1000;
      await persist(tokens.access_token, refreshTok, expiry, email);
      set({ accessToken: tokens.access_token, refreshToken: refreshTok, expiry, email, connected: true });
      toast.success(email ? tr("toast.google.connectedAs", { email }) : tr("toast.google.connected"));
      return true;
    } catch (e) {
      const msg = e instanceof GoogleOAuthError || e instanceof Error ? e.message : String(e);
      toast.error(msg);
      return false;
    } finally {
      set({ busy: false });
    }
  },

  async disconnect() {
    const { refreshToken: rt, accessToken: at } = get();
    if (rt) await revokeToken(rt);
    else if (at) await revokeToken(at);
    await persist(null, null, null, null);
    set({ accessToken: null, refreshToken: null, expiry: null, email: null, connected: false });
  },

  getAccessToken() {
    return get().accessToken;
  },

  async refreshAccess() {
    const { refreshToken: rt } = get();
    if (!rt || !googleConfigured()) return null;
    try {
      const tokens = usesProxy()
        ? await refreshViaProxy(await serverUrl(), { refreshToken: rt })
        : await refreshToken({ refreshToken: rt, clientId: activeGoogleClientId() });
      const newRefresh = tokens.refresh_token || rt; // Google usually omits it on refresh
      const expiry = Date.now() + (tokens.expires_in ?? 3600) * 1000;
      await persist(tokens.access_token, newRefresh, expiry, get().email);
      set({ accessToken: tokens.access_token, refreshToken: newRefresh, expiry });
      return tokens.access_token;
    } catch (e) {
      // Disconnect ONLY on a definitive grant rejection (Google's invalid_grant
      // arrives as 400; the proxy forwards it as 400, transient upstream trouble
      // as 502). A network drop or our proxy cold-starting past the retry budget
      // must NOT drop the tokens — the next reconcile simply retries.
      const status =
        e instanceof GoogleSyncError || e instanceof SyncError ? e.status : undefined;
      if (status === 400 || status === 401 || status === 403) {
        await persist(null, null, null, null);
        set({ accessToken: null, refreshToken: null, expiry: null, email: null, connected: false });
      }
      return null;
    }
  },
}));

async function persist(
  access: string | null,
  refresh: string | null,
  expiry: number | null,
  email: string | null,
): Promise<void> {
  const s = getServices().settings;
  await s.set(K_ACCESS, access ?? "");
  await s.set(K_REFRESH, refresh ?? "");
  await s.set(K_EXPIRY, expiry ? String(expiry) : "");
  await s.set(K_EMAIL, email ?? "");
}

/** Pull the email claim out of the OIDC id_token (we request openid+email). */
function emailFromIdToken(idToken?: string): string | null {
  if (!idToken) return null;
  const seg = idToken.split(".")[1];
  if (!seg) return null;
  try {
    const json = JSON.parse(atob(seg.replace(/-/g, "+").replace(/_/g, "/"))) as { email?: unknown };
    return typeof json.email === "string" ? json.email : null;
  } catch {
    return null;
  }
}
